import { useState } from 'react';
import { Download, ExternalLink, MapPin, Calendar, Eye } from 'lucide-react';
import { getFileIcon, getFileTypeLabel, formatBytes } from '../../utils/fileUtils';
import ThumbnailPreview from '../ThumbnailPreview';
import { IscBadge, PiiBadge, MaoriDataBadge, StatusBadge } from '../badges';
import { BRAND_TEXT } from './constants';
import { ResultSelectHover, ResultSelectionControl } from './ResultCheckbox';
import { rowClickTogglesSelection, type ResultSelectionControls } from './resultSelection';
import { formatDate, getSearchResultGeoLines } from './searchFormat';
import type { SearchResult } from './types';

const ResultCard = ({
  result,
  onDownload,
  navigate,
  selection,
}: {
  result: SearchResult;
  onDownload: (id: string) => void;
  navigate: (path: string) => void;
  selection: ResultSelectionControls;
}) => {
  const [showPreview, setShowPreview] = useState(false);

  const piiDetected = result.personal_information?.detected === true;
  const piiLevel = result.personal_information?.sensitivity_level || 'standard';
  const informationSecurityClassification = result.information_security_classification?.level || 'unclassified';
  const maoriDetected = result.maori_data_sovereignty?.detected === true;
  const createdAt = result.metadata?.created;
  const contentType = result.file?.content_type || '';
  const selected = selection.isSelected(result.id);

  return (
    <div
      className={`group relative bg-white rounded-xl border p-5 hover:shadow-md hover:border-emerald-200 transition-all duration-200 ${
        selected ? 'border-emerald-300 bg-emerald-50/60' : 'border-gray-100'
      } ${selection.active ? 'cursor-pointer' : ''}`}
      onMouseEnter={() => setShowPreview(true)}
      onMouseLeave={() => setShowPreview(false)}
      onClick={(event) => {
        if (!selection.enabled || !selection.active || !rowClickTogglesSelection(event.target)) return;
        selection.onToggle(result.id, event.shiftKey);
      }}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <ResultSelectionControl
            selection={selection}
            recordId={result.id}
            title={result.title}
          />
          <div className="shrink-0">{getFileIcon(contentType, 'w-6 h-6')}</div>
          <div className="min-w-0">
            <a
              href={`/records/${result.id}`}
              onClick={(e) => { if (!e.ctrlKey && !e.metaKey) { e.preventDefault(); navigate(`/records/${result.id}`); } }}
              className={`text-base font-semibold ${BRAND_TEXT} hover:underline truncate block max-w-[500px] text-left`}
              title={result.title}
            >
              {result.title}
            </a>
            <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-400">
              <span>{getFileTypeLabel(contentType)}</span>
              <span>·</span>
              <span>v{result.version || 1}</span>
              {result.file?.size != null && (
                <>
                  <span>·</span>
                  <span>{formatBytes(result.file.size)}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Immutable indicator + Actions */}
        <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          <ResultSelectHover
            selection={selection}
            recordId={result.id}
            title={result.title}
            className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
          />
          <button
            onClick={() => onDownload(result.id)}
            className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
            title="Download"
          >
            <Download className="w-4 h-4" />
          </button>
          <a
            href={`/records/${result.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
            title="View details"
          >
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* Summary */}
      {result.content_summary && (
        <p className="mt-2.5 text-sm text-gray-600 line-clamp-2 leading-relaxed">
          {result.content_summary}
        </p>
      )}

      {/* Meta row */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {getSearchResultGeoLines(result).map((line, idx) => (
          <span
            key={`${idx}-${line}`}
            className="flex items-center gap-1 text-xs text-gray-500"
          >
            <MapPin className="w-3 h-3" />
            {line}
          </span>
        ))}
        {createdAt && (
          <span className="flex items-center gap-1 text-xs text-gray-500">
            <Calendar className="w-3 h-3" />
            {formatDate(createdAt)}
          </span>
        )}
      </div>

      {/* Labels */}
      <div className="mt-2.5 flex flex-wrap gap-1.5">
        <StatusBadge status={result.status} immutabilityFinal={result.immutability?.final} />
        <IscBadge level={informationSecurityClassification} />
        <PiiBadge detected={piiDetected} level={piiLevel} />
        <MaoriDataBadge detected={maoriDetected} />
      </div>

      {/* Preview overlay on hover */}
      {showPreview && (
        <div className="absolute right-0 top-0 translate-x-[calc(100%+8px)] z-50 w-72 bg-white rounded-xl border border-gray-200 shadow-xl p-4 pointer-events-none">
          <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-gray-700">
            <Eye className="w-4 h-4 text-emerald-600" />
            Preview
          </div>
          <ThumbnailPreview
            thumbnailHref={result._links?.thumbnail?.href ?? null}
            className="flex items-center justify-center min-h-[160px] relative overflow-hidden"
            imageClassName="max-w-full max-h-[220px] object-contain rounded-lg"
          />
        </div>
      )}
    </div>
  );
};

export default ResultCard;
